import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import disableRemoteLogin from 'actions/login/disableRemoteLogin.action';
import enableRemoteLogin from 'actions/login/enableRemoteLogin.action';
import getPin from 'actions/pin/getPin.action';
import setPin from 'actions/pin/setPin.action';
import togglePin from 'actions/pin/togglePin.action';
import fetchStylist from 'actions/stylist/fetchStylist.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import toggleCalendarSync from 'actions/stylist/toggleCalendarSync.action';
import { feStripeFoldToggle } from 'actions/ui.actions';
import icoClose from 'assets/images/close-menu_white.svg';
import emailPinIco from 'assets/images/email-pin.svg';
import STRIPE_LOGO from 'assets/images/logos/stripe-logo-blue.png';
import resetPinIco from 'assets/images/reset-pin.svg';
import validateEmail from 'bl/validation/validateEmail.bl';
import DialogMessage from 'components/dialog/DialogMessage.component';
import SalonModal from 'components/dialog/SalonModal.component';
import ButtonModal from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import CardWaitlist from 'components/waitlist/CardWaitlist.component';
import PhoneInput from 'components/wrapped/PhoneInput.component';
import FLAG from 'config/flag.singleton';
import { USE_CALLSYNC_BUTTON } from 'config/use.conf';
import IN from 'constants/in';
import LINK from 'constants/link.const';
import { NO } from 'constants/no';
import DISPLAY_POLICY_TEXT from 'constants/pos/stripePolicyDisplayedText.const';
import TRANSFER_POLICY_TEXT_LOWER from 'constants/pos/stripeTransferPolicyTextLower.const';
import USER_ROLE from 'constants/role';
import S from 'constants/string.const';
import { withTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import selectOnboardingStylistsMap from 'selectors/selectOnboardingStylistsMap.selector';
import getAny from 'utils/accessors/getAny.util';
import getArray from 'utils/accessors/getArray.util';
import getString from 'utils/accessors/getString.util';
import findById from 'utils/arrays/findById.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import fromPermissionToPretty from 'utils/converters/fromPermissionToPretty.util';
import determineFirstNonNil from 'utils/determineFirstNonNil';
import { isEnvMobile } from 'utils/env/determine.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import { isValidPhone } from 'utils/predicates/isValid.util';
import isEmptyString from 'utils/predicates/string/isEmptyString.util';
import { getActiveStylists } from 'utils/salons/getActiveStylists.util';
import equalStrings from 'utils/strings/equalStrings.util';

import InputTypeDropdown from 'onboarding/common/forms/dropdown/dropdown/InputTypeDropdown';
import InputField from 'onboarding/common/InputField';
import EmailTooltipText from 'onboarding/stylists/AddStylist/EmailTooltipText.component';
import PermissionLevelTooltip from 'onboarding/stylists/AddStylist/PermissionLevelTooltip.component';
import { emailPin, resetPin } from 'salon/actions';
import Button from 'settings/page-elements/Button';
import Checkbox from 'settings/page-elements/dropdown/Checkbox';

import CN from './AddStylist.module.scss';

const L = logger('AddStylist');

const FLAG_DISPLAY_ELM = false;
const WAITLIST_HELP_URL = 'https://help.getslick.com/en/articles/2806215-setup-a-wait-list-column';
const EMAIL_INPUT_ID = 'DropdownEmail';

const getTransferPolicyText = (transferPolicy) =>
  DISPLAY_POLICY_TEXT[transferPolicy] || 'composite.stripePolicyDisplayedText.unknownPolicy';

const stateUpdateForRoles = ({ roles, role, roleId, servesAsWaitlist, isSelfEmployed, onRoleChange }) => {
  if (!roles) {
    return {};
  }

  const update = {};

  if (role) {
    update.selectedRole = role;
  } else if (roles.length > 0) {
    // eslint-disable-next-line prefer-destructuring
    update.selectedRole = roles[0];
    if (roleId) {
      for (let i = 0; i < roles.length; i += 1) {
        if (roles[i].id === roleId) {
          update.selectedRole = roles[i];
        }
      }
    }
    onRoleChange(update.selectedRole.id);
  }

  if (servesAsWaitlist) {
    update.serves_as_waitlist = true;
  }

  if (isSelfEmployed) {
    update.selfEmployed = true;
  }

  return update;
};

const determinePinForStylistId = ({ stylistId, props }) => getAny(undefined, ['pinCodes', stylistId, 'pin'], props);
const determineStylistId = (props) => determineFirstNonNil(props.stylistId, getAny(null, 'stylist.id', props));
const determineEmail = (props) => getString('stylist.email', props);

const determinePermission = ({ email, state, props }) => {
  const { permissionName: permissionFromState } = state;
  const { stylist, hasEmailError } = props;

  const { user_id: userId, permission_level: permissionFromProps, email: emailFromProps } = stylist || {};
  const hasEmail = !!fromAnyToString(emailFromProps);
  const emailsWithLogin = getArray('emailsWithLogin', props);
  const foundInfoById = findById(userId, emailsWithLogin);
  const foundPermissionById = foundInfoById && foundInfoById.permission_level;

  if (email) {
    // new email has been attached, if userId exists for it, disregard the standard logic
    const foundInfoByEmail = emailsWithLogin.find((info) => info.email === email);
    if (foundInfoByEmail && foundInfoByEmail.id) {
      const result = foundInfoByEmail.permission_level;
      return L.debug('determinePermission()', 'result:', result);
    }
  }

  const result =
    permissionFromState ||
    (permissionFromProps === USER_ROLE.noAccess ? S.empty : permissionFromProps) ||
    foundPermissionById ||
    (!hasEmailError && hasEmail && USER_ROLE.manager) ||
    USER_ROLE.noAccess;

  return L.debug('determinePermission()', 'result:', result);
};

const TooltipCalSyncText = ({ t }) => (
  <div className={CN.tooltipContainer}>
    <div className={CN.tooltipTitle}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipCalSync.title')}</div>
    <div className={CN.bullet}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipCalSync.bullet1')}</div>
    <div className={CN.bullet}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipCalSync.bullet2')}</div>
  </div>
);

const TooltipRemoteLoginText = ({ t }) => (
  <div className={CN.tooltipContainer}>
    <div className={CN.tooltipTitle}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipRemoteLogin.title')}</div>
    <div className={CN.bullet}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipRemoteLogin.bullet1')}</div>
  </div>
);

class AddStylist extends React.Component {
  static defaultProps = {
    hideWaitlistSectionIfOneStylist: false,
    roles: [],
    roleId: null,
  };

  constructor(props) {
    super(props);

    this.RefJob = React.createRef();
    this.RefEmployment = React.createRef();
    this.RefPermission = React.createRef();
    this.RefEmail = React.createRef();
    this.pinInputRef = React.createRef();
    this.remoteInputRef = React.createRef();
    this.pinFormRef = React.createRef();
    this.inputEmailRef = React.createRef();

    const { stylist, roles, onRoleChange } = props;
    const {
      has_column: hasColumn,
      is_self_employed: isSelfEmployed,
      phone,
      serves_as_waitlist: servesAsWaitlist,
      email,
      role,
      permission_level: permissionLevel,
      has_cal_sync: hasCalSync,
      role_id: roleId,
    } = stylist || {};

    const state = {
      hasColumn: !!hasColumn,
      roleDropdownModal: false,
      serves_as_waitlist: false,
      selfEmployed: false,
      employmentDropdown: false,
      checkedCalSync: hasCalSync || false,
      disableLoginWithEmailModal: false,
      enableLoginWithEmailModal: false,
      missingEmailModal: false,
      enablePinModal: false,
      emailSentModal: false,
      type: 'password',
      resetPinModal: false,
      newPinModal: false,
      isExternal: false,
      isInternal: false,
      emailDropdown: false,
      permissionDropdown: false,
      tooltip: false,
      permissionItems: [
        { id: 0, name: 'common.permissionRoles.receptionist' },
        { id: 1, name: 'common.permissionRoles.manager' },
      ],
      permissionName: permissionLevel || '',
      stylistEmail: email || '',
      phone: phone || '',
      loadingEmail: false,
      loadingPin: false,
      emailInviteSentModal: false,
      tooltipForExistingEmail: false,
      alertSuccess: false,
      emailSentPinCodeModal: false,
      inputEmailValue: '',
      emailHasError: false,
      emailSentText1: '',
      disableCalSyncModal: false,
      renderNoActiveStylistModal: false,
      renderExistingEmailsModal: false,
      confirmEmail: '',
      emailError: '',
    };

    const update = stateUpdateForRoles({
      roles,
      role,
      roleId,
      servesAsWaitlist,
      isSelfEmployed,
      onRoleChange,
    });

    this.state = Object.assign(state, update);
    L.debug('constructor()', { state, props });
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutsideDropdown);

    const email = determineEmail(this.props);

    if (this.pinInputRef && this.pinInputRef.current) {
      const { current } = this.pinInputRef;
      current.disabled = !email;
    }

    if (this.remoteInputRef && this.remoteInputRef.current) {
      this.remoteInputRef.current.disabled = !email;
    }
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    L.debug('componentWillReceiveProps()', nextProps, '<-', this.props);

    const { stylistEmail, permissionName: permissionFromState } = this.state;
    const { stylist: oldStylist } = this.props;
    const { stylist: newStylist } = nextProps;
    const { has_remote_login: oldHasRemoteLogin } = oldStylist || {};
    const {
      has_remote_login: newHasRemoteLogin,
      email: newEmail,
      permission_level: permissionFromProps,
    } = newStylist || {};

    if (this.pinInputRef && this.pinInputRef.current && newEmail) {
      this.pinInputRef.current.disabled = false;
    }

    if (this.remoteInputRef && this.remoteInputRef.current && newEmail) {
      this.remoteInputRef.current.disabled = false;
    }

    if (newHasRemoteLogin && !oldHasRemoteLogin) {
      // have to show this modal only stylist has_remote_login is false
      // this.setState({
      //   emailSentModal: true,
      //   emailSentText1: 'This user has been successfully sent a secure email to create their own password',
      // });
    }

    if (newEmail && !stylistEmail) {
      this.setState({ stylistEmail: newEmail });
    }

    if (permissionFromProps && !permissionFromState) {
      this.setState({ permissionName: permissionFromProps });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutsideDropdown);
  }

  onRoleClick = () => {
    this.setState({ roleDropdownModal: !this.state.roleDropdownModal });
  };

  onRoleDropdownClose = () => {
    this.setState({ roleDropdownModal: false });
  };

  onRoleSelect = (item) => {
    let selectedRole;
    if (this.props.roles) {
      for (let i = 0; i < this.props.roles.length; i += 1) {
        if (this.props.roles[i].id === item.id) {
          selectedRole = this.props.roles[i];
          this.props.onRoleChange(this.props.roles[i].id);
          this.setState({ selectedRole, roleDropdownModal: false });
          break;
        }
      }
    }
  };

  onEmploymentTypeSelect = () => {
    this.setState({ employmentDropdown: !this.state.employmentDropdown });
  };

  onClose = () => {
    this.setState({
      enablePinModal: false,
      disableLoginWithEmailModal: false,
      enableLoginWithEmailModal: false,
      resetPinModal: false,
      newPinModal: false,
      emailSentModal: false,
      emailInviteSentModal: false,
      emailSentPinCodeModal: false,
      inputEmailValue: '',
      disableCalSyncModal: false,
    });
  };

  onResetPin = () => {
    this.setState({ resetPinModal: true });
  };

  onResetButtonClick = () => {
    this.setState({ resetPinModal: false });

    if (this.props.stylist && this.props.stylist.id && typeof this.props.stylist.id === 'number') {
      this.props.resetPin(this.props.salonId, this.props.stylist.id);
    }

    const interval = setInterval(() => {
      if (
        this.props.pinCodes &&
        this.props.pinCodes[this.props.stylist.id] &&
        this.props.pinCodes[this.props.stylist.id].pin
      ) {
        clearInterval(interval);
        this.setState({ newPinModal: true });
      }
    }, 500);
  };

  onCheckboxEmailClick = () => {
    const { stylist } = this.props;
    const { has_remote_login: hasRemoteLogin, email } = stylist;

    if (!hasRemoteLogin && !email) return this.setState({ missingEmailModal: true });
    if (!hasRemoteLogin) return this.setState({ disableLoginWithEmailModal: true });
    return this.setState({ enableLoginWithEmailModal: true });
  };

  onCheckboxCalSync = () => {
    const { checkedCalSync } = this.state;
    const { stylist } = this.props;
    const { email } = stylist;

    if (!email) return this.setState({ missingEmailModal: true });
    return checkedCalSync ? this.setState({ disableCalSyncModal: true }) : this.enableCalSync();
  };

  onSalonPhoneNumberChange = (value) => {
    this.props.onPhoneChange(value);
    this.setState({ phone: value });
  };

  onPinSectionClick = () => {
    L.debug('onPinSectionClick()');
    this.setState({ tooltip: true });
    setTimeout(() => this.setState({ tooltip: false }), 5000);
  };

  setSelfEmployed = (tf) => {
    // only employed or self-employed can be ticked at once (ie one or the other)
    this.setState({ selfEmployed: tf }, () => {
      this.props.onSelfEmployedChange(this.state.selfEmployed);
      this.setState({ employmentDropdown: false });
    });
  };

  setInternal = () => void this.setState({ isExternal: false, isInternal: true });
  setExternal = () => void this.setState({ isExternal: true, isInternal: false });

  setWaitList = () => {
    const { stylists, stylist } = this.props;
    const isWaitlist = stylist.serves_as_waitlist;

    const activeStylistsWithoutWaitlist = getActiveStylists(stylists);
    // If there is only one stylist that is not waitlisted and we are trying to change it, show modal
    if (activeStylistsWithoutWaitlist.length === 1 && !isWaitlist) {
      this.setState({ renderNoActiveStylistModal: true });
    } else {
      this.setState(
        (prevState) => ({
          serves_as_waitlist: !prevState.serves_as_waitlist,
        }),
        () => {
          this.props.onWaitlistChange(this.state.serves_as_waitlist);
        },
      );
    }
  };

  closeDeleteStylistModal = () => void this.setState({ renderNoActiveStylistModal: false });

  selectPermission = (permissionName) => {
    this.setState({ permissionDropdown: false, permissionName });
    this.props.onPermissionLevelChange(permissionName.toLowerCase());
  };

  handlePinCheckboxClick = () => {
    const email = determineEmail(this.props);
    L.debug('handlePinCheckboxClick()', { email });
    if (!email) {
      this.setState({ tooltip: true });
      setTimeout(() => this.setState({ tooltip: false }), 5000);
      return;
    }

    L.unignore('handlePinCheckboxClick()', this.togglePinOnOff());
  };

  handlePermissionClick = () => void this.setState({ permissionDropdown: !this.state.permissionDropdown });

  handleClickAddNewEmail = () => {
    const emailInput = document.getElementById(EMAIL_INPUT_ID);

    if (emailInput) {
      emailInput.focus();
      this.setState({ stylistEmail: '' });
    }
    this.setState({ emailDropdown: !this.state.emailDropdown });
  };

  toggleEmailDropdown = (inputEmail) => {
    const { emailDropdown, permissionName } = this.state;
    const { validateEmail: onValidateEmail, isAddingNewUser } = this.props;

    L.debug('toggleEmailDropdown()', { isAddingNewUser, emailDropdown, inputEmail });

    const stylistEmail = fromAnyToString(inputEmail);
    if (stylistEmail) {
      this.setState({ stylistEmail });
    }
    this.setState({ emailDropdown: !emailDropdown });

    const emailComponent = this.RefDropdowns && this.RefDropdowns.current;
    if (!emailComponent) {
      return void L.warn('toggleEmailDropdown()', 'Invalid emailComponent:', emailComponent);
    }

    const effectivePermission = determinePermission({ email: stylistEmail, state: this.state, props: this.props });

    if (permissionName !== effectivePermission) {
      // attaching new email should pre-set the permission field with the BE preferred value
      L.info('toggleEmailDropdown()', 'Changing permission to', effectivePermission, 'from', permissionName);
      // we want to set BE preferred value when add new stylist with add new email
      this.selectPermission(effectivePermission);
    }

    const email = determineEmail(this.props);
    const stylistId = determineStylistId(this.props);
    if (stylistId && stylistId !== NO.id && email) {
      return void this.setState({ emailDropdown: false, tooltipForExistingEmail: true });
    }

    L.debug('toggleEmailDropdown()', stylistEmail, email);

    if (!email || !this.pinInputRef || !this.pinInputRef.current || this.pinInputRef.current.disabled) {
      return void this.setState({ tooltipForExistingEmail: false });
    }

    const errorMessage = onValidateEmail(email);
    if (isAddingNewUser && email && errorMessage) {
      return void this.setState({ tooltipForExistingEmail: false });
    }
    return void this.setState({ emailDropdown: true, tooltipForExistingEmail: false });
  };

  handleClickOutsideDropdown = (e) => {
    if (this.RefJob && this.RefJob.current && !this.RefJob.current.contains(e.target)) {
      this.setState({
        roleDropdownModal: false,
      });
    }

    if (this.RefEmployment && this.RefEmployment.current && !this.RefEmployment.current.contains(e.target)) {
      this.setState({
        employmentDropdown: false,
      });
    }
    if (this.RefPermission && this.RefPermission.current && !this.RefPermission.current.contains(e.target)) {
      this.setState({
        permissionDropdown: false,
      });
    }

    if (this.RefEmail && this.RefEmail.current && !this.RefEmail.current.contains(e.target)) {
      this.setState({
        emailDropdown: false,
      });
    }
  };

  togglePinOnOff = async () => {
    const { stylist, salonId, setPin, togglePin, fetchStylist, fetchStylistsBySalon } = this.props;

    // NOTE: BE sends null if pin was never set, false if it was set then disabled, true if set and enabled
    const hasPin = stylist.has_pin_login;
    L.debug('togglePinOnOff()', hasPin);

    if (!this.state.enablePinModal) {
      this.setState({ enablePinModal: true });
      return;
    }

    const stylistId = determineStylistId(this.props);
    if (!stylistId || typeof stylistId !== 'number') {
      return;
    }

    this.setState({ enablePinModal: false, loadingPin: true });
    const options = { salonId, stylistId, pinStatus: !hasPin };
    // if pin was not set before (null status) need to call setPin() action, otherwise togglePin()
    const result = await (hasPin === null ? setPin(options) : togglePin(options));
    this.setState({ loadingPin: false });

    // TODO: provide user notification for success or failure here, check the result for more info
    if (fromAnyToString(result.type).endsWith('_FAILURE')) {
      L.warn('togglePinOnOff()', 'Setting pin failed', result.errorMessage);
    } else {
      L.debug('togglePinOnOff()', 'Setting pin succeeded with result:', result);
    }

    try {
      const result = await fetchStylist({ salonId, stylistId });
      L.debug('togglePinOnOff()', 'Fetching stylist succeeded with result:', result);
    } catch (e) {
      L.warn('togglePinOnOff()', 'Fetching stylist failed.', e);
    }

    // not interested if/how this one finishes at this moment, so no await for it
    L.unignore('togglePinOnOff()', fetchStylistsBySalon(this.props.salonId));
  };

  sentEmailPin = () => {
    this.setState({ emailSentPinCodeModal: true });
    if (this.props.stylist.id) {
      this.props.emailPin(this.props.salonId, this.props.stylist.id);
    }
  };

  toggleEmailLogin = async () => {
    const { enableLoginWithEmailModal, disableLoginWithEmailModal } = this.state;
    const { salonId, enableRemoteLogin, disableRemoteLogin, fetchStylistsBySalon, fetchStylist, stylist } = this.props;
    const stylistId = determineStylistId(this.props);

    if (stylistId) {
      if (enableLoginWithEmailModal) {
        const disableResult = await disableRemoteLogin({ salonId, stylistId });
        L.warn('toggleEmailLogin()', { disableResult });
      } else if (disableLoginWithEmailModal) {
        const enableResult = await enableRemoteLogin({ salonId, stylistId });

        const inviteEmail = enableResult.response.invite_email;

        if (inviteEmail === false) {
          this.setState({ emailInviteSentModal: true });
        } else if (stylist.has_remote_login) {
          this.setState({
            emailSentModal: false,
          });
        } else {
          this.setState({
            emailSentModal: true,
            emailSentText1: this.props.t('stylistScreen.addStylist.emailSentModal.description1'),
          });
        }
      }
    } else {
      L.warn('toggleEmailLogin()', 'Invalid stylistId:', stylistId);
    }

    this.setState({ disableLoginWithEmailModal: false, enableLoginWithEmailModal: false });

    try {
      this.setState({ loadingEmail: true });
      const fetchStylistResult = await fetchStylist({ salonId, stylistId });
      L.warn('toggleEmailLogin()', 'fetchStylistResult:', fetchStylistResult);

      L.unignore('toggleEmailLogin()', fetchStylistsBySalon(salonId));
      // NOTE: no catch here because handleToggleEmail will
    } finally {
      this.setState({ loadingEmail: false });
    }
  };

  handleCalSyncEmailChange = (value) => void this.setState({ inputEmailValue: value.target.value });

  // FIXME: emailHasError should be boolean, not contain the message
  validateCalSyncEmail = (value) =>
    void this.setState({
      emailHasError: validateEmail({ value }) ? '' : this.props.t('common.errors.validation.notValidEmail'),
    });

  handleSendCalSyncEmail = () => {
    this.setState({
      emailSentText1: this.props.t('stylistScreen.addStylist.emailSentModal.description2'),
      emailSentModal: true,
    });
  };

  enableCalSync = () => {
    const {
      toggleCalendarSync,
      stylist: { id },
      salonId,
    } = this.props;

    L.unignore(
      'enableCalSync()',
      'toggleCalendarSync:',
      toggleCalendarSync({ stylistId: id, salonId, syncEnabled: true }),
    );

    this.setState(
      {
        emailSentText1: this.props.t('stylistScreen.addStylist.emailSentModal.description2'),
        emailSentModal: true,
      },
      () => this.setState({ checkedCalSync: true }),
    );
  };

  disableCalSync = () => {
    const {
      toggleCalendarSync,
      stylist: { id },
      salonId,
    } = this.props;

    L.unignore(
      'disableCalSync()',
      'toggleCalendarSync:',
      toggleCalendarSync({ stylistId: id, salonId, syncEnabled: false }),
    );

    this.setState({ disableCalSyncModal: false }, () => this.setState({ checkedCalSync: false }));
  };

  save = async () => {
    const { isInternal: hasColumn, permissionName: permission } = this.state;
    const { salonId, fetchStylist, onAddNewStylistBtnClick } = this.props;
    const stylistId = determineStylistId(this.props);

    L.debug('save()', { stylistId, hasColumn, permission });

    try {
      // no need of fetchStylistsBySalon here, the parent component will handle that
      const saveResult = await onAddNewStylistBtnClick({ stylistId: NO.id, permission, hasColumn });
      L.debug('save()', 'saveResult:', saveResult);

      // TODO: add different messages for success and failure based on saveResult
      // NOTE: in case of adding a new stylist, the accurate ID can be determined from the response itself
      const actualStylistId = stylistId && stylistId > 0 ? stylistId : (saveResult.response || {}).id;

      if (actualStylistId && actualStylistId > 0) {
        const fetchStylistResult = await fetchStylist({ salonId, stylistId: actualStylistId });
        L.debug('save()', 'fetchStylistResult:', fetchStylistResult);
      }
    } finally {
      this.notifySuccess();
    }
  };

  notifySuccess = () => {
    this.setState({ alertSuccess: true });
    setTimeout(() => this.setState({ alertSuccess: false }), 3000);
  };

  showHidePin = async () => {
    const { salonId, getPin } = this.props;
    const stylistId = determineStylistId(this.props);

    if (!stylistId) {
      return;
    }

    // TODO: display error message to user should this fail?
    await getPin({ salonId, stylistId });
    setTimeout(() => this.setState({ type: this.state.type === 'text' ? 'password' : 'text' }), 500);
  };

  handleTogglePin = () => void L.unignore('handleTogglePin()', this.togglePinOnOff());
  handleToggleEmail = () => void L.unignore('handleToggleEmail()', this.toggleEmailLogin());

  handleSave = () => {
    const existingEmails = this.props.stylists?.map((s) => s?.email) || [];
    const currentEmail = this.state.confirmEmail;

    if (currentEmail) {
      const isDuplicate = existingEmails.some((email) => equalStrings(email, currentEmail));

      if (isDuplicate) {
        this.setState({ renderExistingEmailsModal: true });
        return;
      }
    }

    L.unignore('handleSave()', this.save());
  };

  handleEmailToggle = () => void this.toggleEmailDropdown();

  onConfirmEmailChange = (confirmEmail) => {
    this.setState({ confirmEmail }, this.validateEmailMatch);
  };

  validateEmailMatch = () => {
    const { confirmEmail } = this.state;
    const { editedStylist } = this.props;

    if (editedStylist.email && confirmEmail) {
      if (editedStylist.email !== confirmEmail) {
        this.setState({
          emailError: this.props.t('stylistScreen.addStylist.emailError1'),
        });
      } else {
        this.setState({ emailError: '' });
      }
    } else if (editedStylist.email && !confirmEmail) {
      this.setState({ emailError: this.props.t('stylistScreen.addStylist.emailError2') });
    } else {
      this.setState({ emailError: '' });
    }
  };

  handleClickStripe = () => {
    const { salonId } = this.props;
    this.props.feStripeFoldToggle();
    navFor(this.props).push({
      pathname: linkTo({ pattern: LINK.salonOnline, params: { salonId } }),
    });
  };

  render() {
    const isMobile = isEnvMobile();

    const {
      selfEmployed,
      stylistEmail: emailFromState,
      loadingPin,
      newPinModal,
      type,
      phone,
      permissionDropdown,
      resetPinModal,
      permissionName: permissionFromState,
      loadingEmail,
      isExternal,
      selectedRole,
      emailDropdown,
      roleDropdownModal,
      enableLoginWithEmailModal,
      enablePinModal,
      disableLoginWithEmailModal,
      isInternal,
      emailSentModal,
      tooltip,
      permissionItems,
      serves_as_waitlist: servesAsWaitlist,
      employmentDropdown,
      tooltipForExistingEmail,
      hasColumn,
      alertSuccess,
      emailSentPinCodeModal,
      inputEmailValue,
      emailHasError,
      emailSentText1,
      checkedCalSync,
      disableCalSyncModal,
      renderNoActiveStylistModal,
      renderExistingEmailsModal,
      confirmEmail,
      emailError,
    } = this.state;

    const {
      validateName,
      stylist,
      editedStylist,
      onNameChange,
      isAddingNewUser: isAddMode,
      saveNewBtnName,
      onEmailChange,
      validateNameLength,
      roles,
      hasEmailError,
      isSaved,
      validateEmail: onValidateEmail,
      isPostingLoader,
      isDarkMode,
      t,
    } = this.props;

    const emailSentText2 = t('stylistScreen.addStylist.emailSentModal.description3');

    const {
      has_pin_login: hasPinLogin,
      has_remote_login: hasRemoteLogin,
      email,
      id: stylistId,
      user_id: userId,
      transfer_policy: transferPolicy,
    } = stylist || {};

    const { user_name: editedStylistUserName } = editedStylist || {};

    // quirky old behavior of having the state of this component exist outside in parents
    const userName = editedStylistUserName || '';
    const emailsWithLogin = getArray('emailsWithLogin', this.props);
    const pin = determinePinForStylistId({ stylistId, props: this.props });

    // the email value is more current, but if missing, try the one passed down through props
    const effectiveEmail = email || emailFromState;
    const isEmailChecked = !!hasRemoteLogin;
    const effectivePermission = determinePermission({ email: false, state: this.state, props: this.props });

    const selectedRoleName = selectedRole && selectedRole.name;
    const selectedRoleId = selectedRole && selectedRole.id;
    const roleItems = roles
      ? roles.map(({ id, name }) => ({
          id,
          name,
          active: selectedRole ? selectedRoleId === id : false,
        }))
      : undefined;

    const isEditMode = !(isAddMode || !stylistId);
    const isEmailFieldsEmpty = !email && !confirmEmail;

    const isUserNameEmpty = isEmptyString(userName) || !userName?.trim();

    const disablePinForm =
      userId === undefined ||
      isUserNameEmpty ||
      hasEmailError ||
      !stylistId ||
      permissionFromState === USER_ROLE.noAccess;

    const isSaveNewStylistDisabled =
      hasEmailError ||
      isUserNameEmpty ||
      (email && !confirmEmail) || // if only the first email is filled in
      (['manager', 'receptionist'].includes(effectivePermission?.toLowerCase()) && isEmailFieldsEmpty);

    const shouldActivateButton =
      effectivePermission?.toLowerCase() === 'no_access' && isEmailFieldsEmpty && !isUserNameEmpty;

    const isButtonDisabled = isSaveNewStylistDisabled && !shouldActivateButton;

    const showNoEmailMessage =
      ['manager', 'receptionist'].includes(effectivePermission?.toLowerCase()) && !email && !confirmEmail;
    // both emails are empty and there is a role

    return (
      <div
        data-bem="AddStylist"
        className={cn({
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        {alertSuccess && isSaved && <div className="alert success animated fadeInDown">{t('stylistScreen.saved')}</div>}

        <SalonModal tag="AddStylist__ExistingEmails" isOpen={renderExistingEmailsModal} variant="noFooter">
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <ButtonModal variant="empty" onClick={() => void this.setState({ renderExistingEmailsModal: false })}>
                <Icon variant="close" variantInvert />
              </ButtonModal>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.existingEmailsModalTitle.title')}</div>
              <div className={CN.modalText}>{t('stylistScreen.addStylist.existingEmailsModalTitle.description')}</div>
            </div>
          </SalonModal.Body>
        </SalonModal>

        <SalonModal tag="AddStylist" isOpen={renderNoActiveStylistModal}>
          <SalonModal.Body>
            <div className={CN.modalImage}>
              <IconStatus variant="alert">
                <Icon variant="alert" />
              </IconStatus>
            </div>
            <DialogMessage>
              <Space>{t('stylistScreen.addStylist.addStylistWarningModal.descriptionPart1')}</Space>
              <ExternalLink link={WAITLIST_HELP_URL} variant="inline">
                <span className={CN.link}>{t('stylistScreen.addStylist.addStylistWarningModal.linkLabel')}</span>
              </ExternalLink>
              <Space>{t('stylistScreen.addStylist.addStylistWarningModal.descriptionPart2')}</Space>
            </DialogMessage>
          </SalonModal.Body>
          <SalonModal.Footer>
            <ButtonModal variant="primaryForce" casing="dialog" onClick={this.closeDeleteStylistModal}>
              {t('common.buttons.close')}
            </ButtonModal>
          </SalonModal.Footer>
        </SalonModal>

        <SalonModal tag="AddStylist__email-sent" isOpen={emailSentModal} variant="noFooter">
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <ButtonModal variant="empty" onClick={this.onClose}>
                <Icon variant="close" variantInvert />
              </ButtonModal>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant="success">
                <Icon variant="envelope" width="l1" selected />
              </IconStatus>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.emailSentModal.title')}</div>
              <div className={CN.modalText}>{emailSentText1}</div>
              <div className={CN.modalText}>{emailSentText2}</div>
            </div>
          </SalonModal.Body>
        </SalonModal>

        <SalonModal tag="AddStylist__email-sent-pin" isOpen={emailSentPinCodeModal || newPinModal} variant="noFooter">
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <ButtonModal variant="empty" onClick={this.onClose}>
                <Icon variant="close" variantInvert />
              </ButtonModal>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant="success">
                <Icon variant="envelope" width="l1" selected />
              </IconStatus>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.emailSentModal.title')}</div>
              <div className={CN.modalText}>{t('stylistScreen.addStylist.emailSentModal.description4')}</div>
              <br />
              <div className={CN.modalText}>{t('stylistScreen.addStylist.emailSentModal.description5')}</div>
            </div>
          </SalonModal.Body>
        </SalonModal>

        <SalonModal tag="AddStylist__email-access-enabled" isOpen={this.state.emailInviteSentModal} variant="noFooter">
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <ButtonModal variant="empty" onClick={this.onClose}>
                <Icon variant="close" variantInvert />
              </ButtonModal>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant="success">
                <Icon variant="envelope" width="l1" selected />
              </IconStatus>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.emailAccessEnabledModal.title')}</div>
              <div className={CN.modalText}>{t('stylistScreen.addStylist.emailAccessEnabledModal.description1')}</div>
              <div className={CN.modalText}>{t('stylistScreen.addStylist.emailAccessEnabledModal.description2')}</div>
            </div>
          </SalonModal.Body>
        </SalonModal>

        <SalonModal tag="AddStylist__reset-pin" isOpen={resetPinModal} withFooter>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant="alert" width="lg">
                <Icon variant="alert" width="l1" />
              </IconStatus>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.resetPinModal.title')}</div>
              <div className={CN.modalText}>
                {t('stylistScreen.addStylist.resetPinModal.description', { user: userName })}
              </div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <ButtonModal variant="outline" xSpace onClick={this.onClose}>
                {t('common.buttons.cancelUpp')}
              </ButtonModal>
              <ButtonModal variant="primary" xSpace onClick={this.onResetButtonClick}>
                {t('stylistScreen.addStylist.resetPinModal.resetButton')}
              </ButtonModal>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        {isPostingLoader && <OverlaySpinner />}

        <SalonModal tag="AddStylist__toggle-pin" isOpen={disableLoginWithEmailModal} withFooter>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant="alert" width="lg">
                <Icon variant="alert" width="l1" />
              </IconStatus>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.togglePinModal.title1')}</div>
              <div className={CN.modalText}>{t('stylistScreen.addStylist.togglePinModal.description1')}</div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <ButtonModal variant="outline" xSpace onClick={this.onClose}>
                {t('common.buttons.cancelUpp')}
              </ButtonModal>
              <ButtonModal variant="primary" xSpace onClick={this.handleToggleEmail}>
                {t('stylistScreen.addStylist.togglePinModal.enableEmailLoginButton')}
              </ButtonModal>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        <SalonModal tag="AddStylist__missing-email" isOpen={this.state.missingEmailModal} variant="noFooter">
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <ButtonModal variant="empty" onClick={() => void this.setState({ missingEmailModal: false })}>
                <Icon variant="close" variantInvert />
              </ButtonModal>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant="alert" width="lg">
                <Icon variant="alert" width="l1" />
              </IconStatus>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.missingEmailModal.title')}</div>
              <div className={CN.modalText}>
                {t('stylistScreen.addStylist.missingEmailModal.descriptionPart1')}
                <span className={CN.textBold}>
                  <Space>{t('stylistScreen.addStylist.missingEmailModal.descriptionPart2')}</Space>
                </span>
                {t('stylistScreen.addStylist.missingEmailModal.descriptionPart3')}
              </div>
            </div>
          </SalonModal.Body>
        </SalonModal>

        <SalonModal tag="AddStylist__toggle-pin" isOpen={enableLoginWithEmailModal} withFooter>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant="alert" width="lg">
                <Icon variant="alert" width="l1" />
              </IconStatus>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.togglePinModal.title2')}</div>
              <div className={CN.modalText}>{t('stylistScreen.addStylist.togglePinModal.description2')}</div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <ButtonModal variant="outline" xSpace onClick={this.onClose}>
                {t('common.buttons.cancelUpp')}
              </ButtonModal>
              <ButtonModal variant="primary" xSpace onClick={this.handleToggleEmail}>
                {t('stylistScreen.addStylist.togglePinModal.disableEmailLoginButton')}
              </ButtonModal>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        <SalonModal tag="AddStylist__toggle-pin" isOpen={enablePinModal} withFooter>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant={hasPinLogin ? 'alert' : 'success'} width="lg">
                <Icon variant={hasPinLogin ? 'alert' : 'confirm'} width="l1" />
              </IconStatus>
              <div className={CN.modalTitle}>
                {hasPinLogin
                  ? t('stylistScreen.addStylist.enabledPinModal.title1')
                  : t('stylistScreen.addStylist.enabledPinModal.title2')}
              </div>

              {hasPinLogin ? (
                <div className={CN.modalText}>{t('stylistScreen.addStylist.enabledPinModal.description1')}</div>
              ) : (
                <>
                  <div className={CN.modalText}>{t('stylistScreen.addStylist.enabledPinModal.description2')}</div>
                  <div className={CN.alertOuterContainer}>
                    <AlertPanel tag="AddStylist__alert" variant="alert">
                      <div className={CN.alertContainer}>
                        <div className={CN.alertTitle}>{t('stylistScreen.important')}</div>
                        <div>{t('stylistScreen.addStylist.enabledPinModal.alertMessage')}</div>
                      </div>
                    </AlertPanel>
                  </div>
                </>
              )}
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <ButtonModal variant="outline" xSpace onClick={this.onClose}>
                {t('common.buttons.cancelUpp')}
              </ButtonModal>
              <ButtonModal variant="primary" xSpace onClick={this.handleTogglePin}>
                {hasPinLogin
                  ? t('stylistScreen.addStylist.enabledPinModal.disablePinButton')
                  : t('common.buttons.doneUpp')}
              </ButtonModal>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        <SalonModal tag="AddStylist__disable-calendar-sync" isOpen={disableCalSyncModal} withFooter>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <IconStatus variant="alert" width="lg">
                <Icon variant="alert" width="l1" />
              </IconStatus>
              <div className={CN.modalTitle}>{t('stylistScreen.addStylist.disableCalendarModal.title')}</div>
              <div className={CN.modalText}>{t('stylistScreen.addStylist.disableCalendarModal.description')}</div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              <ButtonModal variant="outline" xSpace onClick={this.onClose}>
                {t('common.buttons.cancelUpp')}
              </ButtonModal>
              <ButtonModal variant="primary" xSpace onClick={this.disableCalSync}>
                {t('stylistScreen.addStylist.disableCalendarModal.disableButton')}
              </ButtonModal>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        <div data-bem="AddStylist__content" className="content">
          <div id="add-edit-pin-stylist-container">
            <section data-bem="AddStylist__stylist" id="stylist-form">
              <strong className={CN.infoTitle}>
                {isEditMode ? t('stylistScreen.addStylist.title1') : t('stylistScreen.addStylist.title1')}
              </strong>

              {/* EDIT EXISTING STYLIST START */}
              {isEditMode ? (
                <div data-bem="AddStylist__body--edit-mode" id="edit-existing-stylist">
                  <div
                    className={cn({
                      'cfix t-container relative': true,
                      [CN.inputContainer]: true,
                    })}
                  >
                    <InputField
                      data={{
                        id: 'StaffNameInput',
                        label: t('stylistScreen.inputFields.nameLabel'),
                        type: 'text',
                        placeholder: t('stylistScreen.inputFields.namePlaceholder'),
                        inputValue: userName,
                        inputMode: 'text',
                      }}
                      autoFocus
                      onChangeHandler={onNameChange}
                      validateBlur={validateName}
                      validateChange={validateNameLength}
                    />
                    <TooltipForReceptionist where={IN.salon} />
                  </div>
                  {FLAG_DISPLAY_ELM && (
                    <div className="cfix t-container--phone-sfatt  relative">
                      <span className="field-name" style={{ opacity: 0.6, marginTop: '10px', display: 'block' }}>
                        {t('stylistScreen.inputFields.phoneLabel')}
                      </span>

                      <PhoneInput
                        international
                        defaultCountry="GB"
                        placeholder={t('stylistScreen.inputFields.phonePlaceholder')}
                        value={phone}
                        onChange={(value) => {
                          this.onSalonPhoneNumberChange(value);
                        }}
                        autoComplete="off"
                      />
                      {!phone ? (
                        <div className="input-prompt-show">
                          <span />
                        </div>
                      ) : (
                        <div className="input-prompt-show">
                          <span>
                            {isValidPhone({ value: phone }) ? '' : t('common.errors.validation.notValidPhoneNumber')}
                          </span>
                        </div>
                      )}
                      <TooltipForReceptionist where={IN.salon} />
                    </div>
                  )}

                  <div data-bem="AddStylist__email-container--edit-mode" className="cfix t-container relative">
                    {tooltipForExistingEmail && (
                      <div className="disable-edit-email-tooltip">
                        <span
                          role="button"
                          tabIndex="0"
                          onClick={() =>
                            this.setState({
                              tooltipForExistingEmail: false,
                            })
                          }
                        >
                          <img src={icoClose} alt="Exit anyway" />
                        </span>
                        <EmailTooltipText />
                      </div>
                    )}
                    <div
                      data-bem="AddStylist__email--edit-mode"
                      className={cn({
                        'dropdown-stylist email-input': true,
                        [CN.inputContainer]: true,
                      })}
                      ref={this.RefDropdowns}
                    >
                      <div className="relative" ref={this.inputEmailRef}>
                        <div className="toggle-email-btn" role="button" tabIndex="0" onClick={this.handleEmailToggle}>
                          <span className="select-button-icon">
                            <span className="ic-sml ic_expand_more_black" />
                          </span>
                        </div>

                        <InputField
                          data={{
                            id: EMAIL_INPUT_ID,
                            label: t('stylistScreen.inputFields.emailLabel'),
                            type: 'email',
                            placeholder: 'name@salon.com',
                            inputValue: fromAnyToString(effectiveEmail).toLocaleLowerCase(),
                            inputMode: 'email',
                          }}
                          onChangeHandler={(email) => onEmailChange({ email, userId: undefined })}
                          validateBlur={onValidateEmail}
                        ></InputField>
                      </div>

                      {emailDropdown && (
                        <div data-bem="AddStylist__email-dropdown-container" className="item-list" ref={this.RefEmail}>
                          <div className="scrollable-container">
                            {!emailsWithLogin.length && <div className="popup-list-item">{email || ''}</div>}
                            {emailsWithLogin.map(({ id, email }) => (
                              // eslint-disable-next-line jsx-a11y/interactive-supports-focus
                              <div
                                data-bem="AddStylist__email-dropdown-item"
                                key={id}
                                role="button"
                                className="popup-list-item"
                                onClick={() => {
                                  onEmailChange({ userId: id, email });
                                  this.toggleEmailDropdown(email);
                                }}
                              >
                                {email}
                              </div>
                            ))}
                          </div>

                          {permissionFromState === USER_ROLE.noAccess ? (
                            <Tooltip variant="top" content={t('stylistScreen.addStylist.editMode.emailTooltipContent')}>
                              <div className="popup-list-item fixed-in-list">
                                {t('stylistScreen.addStylist.addEmailButton')}
                              </div>
                            </Tooltip>
                          ) : (
                            <div
                              className="popup-list-item fixed-in-list"
                              role="button"
                              tabIndex="0"
                              onClick={this.handleClickAddNewEmail}
                            >
                              {t('stylistScreen.addStylist.addEmailButton')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <TooltipForReceptionist where={IN.salon} />
                  </div>

                  <div>
                    {selectedRole && (
                      <>
                        {hasColumn && (
                          <div
                            data-bem="AddStylist__role-container--edit-mode-internal"
                            className={cn({
                              'cfix t-container relative': true,
                              [CN.inputContainer]: true,
                            })}
                          >
                            <div className="dropdown-stylist scrollable-container" ref={this.RefJob}>
                              <span className="dropdown-name">{t('stylistScreen.inputFields.jobLabel')}</span>
                              <label htmlFor="StaffRoleSelect" className="settings-heading">
                                <button
                                  className="utl-full-width select-button"
                                  id="StaffRoleSelect"
                                  onClick={this.onRoleClick}
                                  style={{ marginTop: 5 }}
                                >
                                  <span className="select-button-text">{selectedRole.name}</span>
                                  <span className="select-button-icon">
                                    <span className="ic-sml ic_expand_more_black" />
                                  </span>
                                </button>
                              </label>
                              <TooltipForReceptionist where={IN.roleDropdown} />
                              {roleItems && roleDropdownModal && (
                                <InputTypeDropdown
                                  items={roleItems}
                                  onClose={this.onRoleDropdownClose}
                                  onSelectItem={this.onRoleSelect}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {hasColumn && (
                      <div
                        data-bem="AddStylist__employment-container--edit-mode-internal"
                        className={cn({
                          'cfix t-container relative': true,
                          [CN.inputContainer]: true,
                        })}
                      >
                        <div className="dropdown-stylist">
                          <span className="dropdown-name">{t('stylistScreen.inputFields.employmentLabel')}</span>
                          <label htmlFor="EmploymentType" className="settings-heading">
                            <button
                              className="utl-full-width select-button"
                              onClick={this.onEmploymentTypeSelect}
                              style={{ marginTop: 5 }}
                            >
                              <span className="select-button-text">
                                {selfEmployed
                                  ? t('stylistScreen.addStylist.selfEmployed')
                                  : t('stylistScreen.addStylist.employed')}
                              </span>
                              <span className="select-button-icon">
                                <span className="ic-sml ic_expand_more_black" />
                              </span>
                            </button>
                          </label>
                          {employmentDropdown && (
                            <div className="employment-dropdown" ref={this.RefEmployment}>
                              <div className="wait-list-checkbox">
                                <div className="label-div">
                                  <label htmlFor="Employed">
                                    <Checkbox
                                      isChecked={!selfEmployed}
                                      id="Employed"
                                      onCheckboxClick={() => this.setSelfEmployed(false)}
                                    />
                                    <span>{t('stylistScreen.addStylist.employed')}</span>
                                  </label>
                                </div>
                              </div>
                              <div className="wait-list-checkbox">
                                <div className="label-div">
                                  <label htmlFor="selfImployed">
                                    <Checkbox
                                      isChecked={selfEmployed}
                                      id="selfImployed"
                                      onCheckboxClick={() => this.setSelfEmployed(true)}
                                    />
                                    <span>{t('stylistScreen.addStylist.selfEmployed')}</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <TooltipForReceptionist where={IN.roleDropdown} />
                      </div>
                    )}
                  </div>

                  <div
                    data-bem="AddStylist__permission-container--edit-mode"
                    className={cn({
                      'cfix t-container relative': true,
                      [CN.inputContainer]: true,
                    })}
                  >
                    <div className="dropdown-stylist">
                      <span className="dropdown-name">
                        {t('stylistScreen.inputFields.permissionLabel')}
                        <PermissionLevelTooltip />
                      </span>
                      <label htmlFor="PermissionLevel" className="settings-heading">
                        <button
                          className="utl-full-width select-button"
                          id="PermissionLevel"
                          onClick={this.handlePermissionClick}
                          style={{ marginTop: 5 }}
                        >
                          <span className="select-button-text">{fromPermissionToPretty(effectivePermission)}</span>

                          <span className="select-button-icon">
                            <span className="ic-sml ic_expand_more_black" />
                          </span>
                        </button>
                      </label>

                      {permissionItems && permissionDropdown && (
                        <div className="item-list" ref={this.RefPermission}>
                          {permissionItems.map(({ id, name }) => (
                            <div
                              className="popup-list-item"
                              role="button"
                              tabIndex="0"
                              key={id}
                              onClick={() => this.selectPermission(t(name))}
                            >
                              {t(name)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <TooltipForReceptionist where={IN.roleDropdown} />
                  </div>
                  {/* WAITLIST CHECKBOX*/}
                  <ResponsiveBox.Desktop>
                    {(isInternal || stylistId) && servesAsWaitlist && hasColumn && (
                      <div data-bem="AddStylist__waitlist-toggler">
                        <div className={CN.waitlistTogglerContainer}>
                          <span className={CN.waitlistTogglerLabel}>
                            {t('stylistScreen.addStylist.editMode.waitlistTogglerLabel')}
                          </span>
                          <Toggler onClick={this.setWaitList} selected={servesAsWaitlist} />
                          <TooltipForReceptionist where={IN.inWaitlist} />
                        </div>
                        <div className={CN.waitlistDescription}>
                          {t('stylistScreen.addStylist.editMode.waitlistDescription')}
                        </div>
                      </div>
                    )}
                  </ResponsiveBox.Desktop>
                </div>
              ) : (
                isAddMode && (
                  <div data-bem="AddStylist__column-option-body" className="staff-type">
                    {/* ADD NEW STYLIST RADIO // EDIT EXISTING STYLIST END */}
                    <span>
                      {t('stylistScreen.addStylist.addMode.descriptionPart1')} <br />
                      {t('stylistScreen.addStylist.addMode.descriptionPart2')}
                    </span>
                    <div
                      role="button"
                      tabIndex={0}
                      id="internal-staff"
                      onClick={this.setInternal}
                      className={isInternal ? 'active' : 'non'}
                    >
                      <span className="fake-check" />
                      {t('stylistScreen.addStylist.addMode.needsColumnCheckbox')}
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      id="external-staff"
                      onClick={this.setExternal}
                      className={isExternal ? 'active' : 'none'}
                    >
                      <span className="fake-check" />
                      {t('stylistScreen.addStylist.addMode.accessOnlyCheckbox')}
                    </div>
                  </div>
                )
              )}

              {/*  ADD NEW STYLIST */}

              {(isInternal || isExternal) && (
                <div data-bem="AddStylist__body--add-mode">
                  <div className={CN.inputContainer}>
                    <InputField
                      data={{
                        id: 'StaffNameInput',
                        label: t('stylistScreen.inputFields.nameLabel'),
                        type: 'text',
                        placeholder: t('stylistScreen.inputFields.namePlaceholder'),
                        inputValue: userName,
                        inputMode: 'text',
                      }}
                      autoFocus
                      onChangeHandler={onNameChange}
                      validateBlur={validateName}
                      validateChange={validateNameLength}
                    />
                    <TooltipForReceptionist where={IN.salon} />
                  </div>
                  {FLAG_DISPLAY_ELM && (
                    <div className={CN.inputContainer}>
                      <span className="field-name" style={{ opacity: 0.6, marginTop: '10px', display: 'block' }}>
                        {t('stylistScreen.inputFields.phoneLabel')}
                      </span>

                      <PhoneInput
                        international
                        defaultCountry="GB"
                        placeholder={t('stylistScreen.inputFields.phonePlaceholder')}
                        value={phone}
                        onChange={(value) => {
                          this.onSalonPhoneNumberChange(value);
                        }}
                        autoComplete="off"
                      />
                      {!phone ? (
                        <div className="input-prompt-show">
                          <span />
                        </div>
                      ) : (
                        <div className="input-prompt-show">
                          <span>
                            {phone && isValidPhone({ value: phone })
                              ? ''
                              : t('common.errors.validation.notValidPhoneNumber')}
                          </span>
                        </div>
                      )}
                      <TooltipForReceptionist where={IN.salon} />
                    </div>
                  )}
                  <div data-bem="AddStylist__email-container--add-mode" className={CN.inputContainer}>
                    {tooltipForExistingEmail && (
                      <div className="disable-edit-email-tooltip">
                        <span
                          role="button"
                          tabIndex="0"
                          onClick={() =>
                            this.setState({
                              tooltipForExistingEmail: false,
                            })
                          }
                        >
                          <img src={icoClose} alt="Exit anyway" />
                        </span>
                        <EmailTooltipText />
                      </div>
                    )}
                    <div
                      data-bem="AddStylist__email--add-mode"
                      className="dropdown-stylist email-input"
                      ref={this.RefDropdowns}
                    >
                      <div className="toggle-email-btn" role="button" tabIndex="0" onClick={this.handleEmailToggle}>
                        <span className="select-button-icon">
                          <span className="ic-sml ic_expand_more_black" />
                        </span>
                      </div>
                      <InputField
                        data={{
                          id: EMAIL_INPUT_ID,
                          label: t('stylistScreen.inputFields.emailLabel'),
                          type: 'email',
                          placeholder: 'name@salon.com',
                          inputValue: fromAnyToString(email),
                          inputMode: 'email',
                        }}
                        onChangeHandler={(email) => {
                          onEmailChange({ email, userId: undefined });
                          this.validateEmailMatch();
                        }}
                        validateBlur={onValidateEmail}
                      />

                      {emailDropdown && (
                        <div className="item-list" ref={this.RefEmail}>
                          <div className="scrollable-container">
                            {emailsWithLogin.length > 0 &&
                              emailsWithLogin.map(({ id, email }) => (
                                <div
                                  key={id}
                                  data-id={id}
                                  tabIndex={0}
                                  role="button"
                                  className="popup-list-item"
                                  onClick={() => {
                                    onEmailChange({ userId: id, email });
                                    this.toggleEmailDropdown(email);
                                    this.setState({ emailDropdown: false });
                                  }}
                                >
                                  {email}
                                </div>
                              ))}
                          </div>
                          <div
                            className="popup-list-item fixed-in-list"
                            role="button"
                            tabIndex="0"
                            onClick={this.handleClickAddNewEmail}
                          >
                            {confirmEmail
                              ? t('stylistScreen.addStylist.editEmail')
                              : t('stylistScreen.addStylist.addEmailButton')}
                          </div>
                        </div>
                      )}
                    </div>
                    <TooltipForReceptionist where={IN.salon} />
                  </div>
                  <div data-bem="AddStylist__email-confirmation-container--add-mode" className={CN.inputContainer}>
                    <InputField
                      data={{
                        label: t('stylistScreen.inputFields.reEnterEmailLabel'),
                        type: 'email',
                        placeholder: 'name@salon.com',
                        inputValue: fromAnyToString(confirmEmail),
                        inputMode: 'email',
                      }}
                      onChangeHandler={this.onConfirmEmailChange}
                      validateBlur={this.validateEmailMatch}
                    />
                    {emailError && <span className={CN.error}>{emailError}</span>}
                  </div>

                  {isInternal && (
                    <div data-bem="AddStylist__role-container--add-mode" className="for-new-int-staff">
                      {selectedRole && (
                        <div
                          className={cn({
                            'cfix t-container relative': true,
                            [CN.inputContainer]: true,
                          })}
                        >
                          <div className="dropdown-stylist scrollable-container" ref={this.RefJob}>
                            <span className="dropdown-name">{t('stylistScreen.inputFields.jobLabel')}</span>
                            <label htmlFor="StaffRoleSelect" className="settings-heading">
                              <button
                                className="utl-full-width select-button"
                                id="StaffRoleSelect"
                                onClick={this.onRoleClick}
                                style={{ marginTop: 5 }}
                              >
                                <span className="select-button-text">{selectedRoleName}</span>
                                <span className="select-button-icon">
                                  <span className="ic-sml ic_expand_more_black" />
                                </span>
                              </button>
                            </label>
                            <TooltipForReceptionist where={IN.roleDropdown} />
                            {roleItems && roleDropdownModal && (
                              <InputTypeDropdown
                                items={roleItems}
                                onClose={this.onRoleDropdownClose}
                                onSelectItem={this.onRoleSelect}
                              />
                            )}
                          </div>
                        </div>
                      )}

                      <div
                        className={cn({
                          'cfix t-container relative': true,
                          [CN.inputContainer]: true,
                        })}
                      >
                        <div className="dropdown-stylist">
                          <span className="dropdown-name">{t('stylistScreen.inputFields.employmentLabel')}</span>
                          <label htmlFor="EmploymentType" className="settings-heading">
                            <button
                              className="utl-full-width select-button"
                              onClick={this.onEmploymentTypeSelect}
                              style={{ marginTop: 5 }}
                            >
                              <span className="select-button-text">
                                {selfEmployed
                                  ? t('stylistScreen.addStylist.selfEmployed')
                                  : t('stylistScreen.addStylist.employed')}
                              </span>
                              <span className="select-button-icon">
                                <span className="ic-sml ic_expand_more_black" />
                              </span>
                            </button>
                          </label>
                          {employmentDropdown && (
                            <div className="employment-dropdown" ref={this.RefEmployment}>
                              <div className="wait-list-checkbox">
                                <div className="label-div">
                                  <label htmlFor="Employed">
                                    <Checkbox
                                      isChecked={!selfEmployed}
                                      id="Employed"
                                      onCheckboxClick={() => this.setSelfEmployed(false)}
                                    />
                                    <span>{t('stylistScreen.addStylist.employed')}</span>
                                  </label>
                                </div>
                              </div>
                              <div className="wait-list-checkbox">
                                <div className="label-div">
                                  <label htmlFor="selfImployed">
                                    <Checkbox
                                      isChecked={selfEmployed}
                                      id="selfImployed"
                                      onCheckboxClick={() => this.setSelfEmployed(true)}
                                    />
                                    <span>{t('stylistScreen.addStylist.selfEmployed')}</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <TooltipForReceptionist where={IN.roleDropdown} />
                      </div>
                    </div>
                  )}

                  <div
                    data-bem="AddStylist__permission-container--add-mode"
                    className={cn({
                      'cfix t-container relative': true,
                      [CN.inputContainer]: true,
                    })}
                  >
                    <div className="dropdown-stylist">
                      <span className="dropdown-name">
                        {t('stylistScreen.inputFields.permissionLabel')}
                        <PermissionLevelTooltip />
                      </span>
                      <label htmlFor="PermissionLevel" className="settings-heading">
                        <button
                          className="utl-full-width select-button"
                          id="PermissionLevel"
                          onClick={this.handlePermissionClick}
                          style={{ marginTop: 5 }}
                        >
                          <span className="select-button-text">{fromPermissionToPretty(effectivePermission)}</span>

                          <span className="select-button-icon">
                            <span className="ic-sml ic_expand_more_black" />
                          </span>
                        </button>
                      </label>

                      {permissionItems && permissionDropdown && (
                        <div className="item-list" ref={this.RefPermission}>
                          {permissionItems.map(({ id, name }) => (
                            <div
                              className="popup-list-item"
                              role="button"
                              tabIndex="0"
                              key={id}
                              onClick={() => this.selectPermission(t(name))}
                            >
                              {t(name)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <TooltipForReceptionist where={IN.roleDropdown} />
                  </div>

                  <div data-bem="AddStylist__save-container" className="sticky-div-on-scroll-add-new-stylist">
                    {showNoEmailMessage && (
                      <p className={CN.error}>
                        {t('stylistScreen.addStylist.noEmailMessagePart1')} <br />
                        {t('stylistScreen.addStylist.noEmailMessagePart2')}
                      </p>
                    )}
                    <Button
                      buttonName={saveNewBtnName}
                      onClickButton={this.handleSave}
                      className="btn-add-new-stylist"
                      disabled={isButtonDisabled}
                    />
                  </div>
                </div>
              )}
            </section>
            <div>
              {/*  PIN VIEW */}
              {(isInternal || isExternal || stylistId) && (
                <div data-bem="AddStylist__pin-and-stripe-container" className={CN.leftContainer}>
                  <section data-bem="AddStylist__pin-body" id="pin-form" ref={this.pinFormRef}>
                    {fromAnyToBool(tooltip) && (
                      <div data-bem="AddStylist__pin-tooltip" className="disable-edit-tooltip">
                        {t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart1')}
                        <strong> {t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart2')} </strong>
                        {t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart3')}{' '}
                        <strong>{t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart4')}</strong>{' '}
                        {t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart5')}
                      </div>
                    )}

                    {disablePinForm && (
                      <div id="disable-pin-form-overlay" role="button" onClick={this.onPinSectionClick} tabIndex={0} />
                    )}

                    <div className={disablePinForm ? 'grayout-elements' : ''}>
                      <strong className={CN.infoTitle}>{t('stylistScreen.addStylist.pinCodeAcces.title')}</strong>
                      <div data-bem="AddStylist__pin-input-container" className="cfix t-container relative">
                        <div className={CN.editStylistTogglerContainer}>
                          <div className={CN.editStylistTogglerLabel}>
                            {t('stylistScreen.addStylist.pinCodeAcces.pinLoginCheckbox')}
                            <div
                              className={cn({
                                [CN.pinLoginStatus]: true,
                                [CN.disabled]: !hasPinLogin,
                              })}
                            >
                              {hasPinLogin ? t('common.enabled').toUpperCase() : t('common.disabled').toUpperCase()}
                            </div>
                          </div>

                          <Toggler onClick={this.handlePinCheckboxClick} selected={hasPinLogin} hideLabel />
                          <TooltipForReceptionist where={IN.roleDropdown} />
                          {loadingPin && (
                            <div className="spinner-container">
                              <div className="load-spinner" />
                            </div>
                          )}
                        </div>
                      </div>

                      {hasPinLogin && (
                        <div className={hasPinLogin ? '' : 'disable-html'}>
                          <div className="cfix t-container relative">
                            <div className="form-horizontal">
                              <label htmlFor="PinCode" className="pin-code-input">
                                <span className="field-name col-md-5" style={{ display: 'block' }}>
                                  {t('stylistScreen.addStylist.pinCodeAcces.pin')}
                                </span>
                                <input id="PinCode" type={type} placeholder="●●●●" value={pin} readOnly />
                                {FLAG.pinCode && (
                                  <div
                                    className="show-hide-pin"
                                    role="button"
                                    tabIndex="0"
                                    onClick={() => void this.showHidePin()}
                                  >
                                    <span className={type === 'password' ? 'Hide' : 'Show'}>
                                      {type === 'password' ? t('common.show') : t('common.hide')}
                                    </span>
                                  </div>
                                )}
                              </label>
                            </div>
                            <TooltipForReceptionist where={IN.roleDropdown} />
                          </div>
                          <div className="cfix t-container relative">
                            <div className="pin-btn-container">
                              <div className="relative">
                                <button onClick={this.onResetPin} className="btn btn-outline" id="reset-pin-btn">
                                  <img
                                    src={resetPinIco}
                                    alt={t('stylistScreen.addStylist.pinCodeAcces.resetPinButton')}
                                    style={{ marginRight: '4px' }}
                                  />
                                  {t('stylistScreen.addStylist.pinCodeAcces.resetPinButton')}
                                </button>
                                <TooltipForReceptionist where={IN.roleDropdown} />
                              </div>
                              <div className="relative">
                                <button onClick={this.sentEmailPin} className="btn btn-outline" id="email-pin-btn">
                                  <img src={emailPinIco} alt="" />
                                  {t('stylistScreen.addStylist.pinCodeAcces.emailPin')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div id="EmailLoginAndCalSync">
                        <div className="cfix t-container ">
                          <strong className="info-title">
                            {t('stylistScreen.addStylist.pinCodeAcces.remoteUserAccess')}
                          </strong>
                        </div>
                        <div className="cfix t-container relative">
                          <div
                            data-bem="AddStylist__remote-login-with-email"
                            className={CN.editStylistTogglerContainer}
                          >
                            <span className={CN.editStylistTogglerLabel}>
                              <TooltipForReceptionist where={IN.roleDropdown} />
                              <Tooltip
                                variant={isMobile ? 'slickRightToBottom' : 'leftToBottom'}
                                width={isMobile ? 'lg' : 'max'}
                                content={<TooltipRemoteLoginText t={t} />}
                              >
                                <div className={CN.iconInfo}>
                                  <Icon variant="info" width="md" />
                                </div>
                              </Tooltip>
                              {t('stylistScreen.addStylist.pinCodeAcces.remoteLoginEmail')}
                            </span>
                            <Toggler onClick={this.onCheckboxEmailClick} selected={isEmailChecked} hideLabel />
                            <TooltipForReceptionist where={IN.inWaitlist} />
                          </div>
                          {loadingEmail && (
                            <div className="spinner-container">
                              <div className="load-spinner" />
                            </div>
                          )}
                        </div>
                        {hasColumn && (
                          <div className="cfix t-container cal-sync-container">
                            <div data-bem="AddStylist__cal-sync" className={CN.editStylistTogglerContainer}>
                              <div className={CN.editStylistTogglerLabel}>
                                <Tooltip
                                  variant={isMobile ? 'slickRightToBottom' : 'leftToBottom'}
                                  width={isMobile ? 'lg' : 'max'}
                                  content={<TooltipCalSyncText t={t} />}
                                >
                                  <div className={CN.iconInfo}>
                                    <Icon variant="info" width="md" />
                                  </div>
                                </Tooltip>
                                {t('stylistScreen.addStylist.pinCodeAcces.tooltipCalSync.title')}
                              </div>
                              <Toggler onClick={this.onCheckboxCalSync} selected={checkedCalSync} hideLabel />
                            </div>

                            {USE_CALLSYNC_BUTTON && checkedCalSync && (
                              <div>
                                <span className="field-name">
                                  {t('stylistScreen.addStylist.pinCodeAcces.calSyncEmailGuide')}
                                </span>
                                <label htmlFor="calSyncEmail" style={{ position: 'relative' }}>
                                  {emailHasError ? (
                                    <span className="error">{t('common.errors.validation.notValidEmail')}</span>
                                  ) : null}
                                  <input
                                    className="utl-full-width"
                                    id="calSyncEmail"
                                    type="text"
                                    placeholder=" "
                                    autoComplete="off"
                                    onChange={this.handleCalSyncEmailChange}
                                    onBlur={(e) => void this.validateCalSyncEmail(e.target.value)}
                                    value={inputEmailValue}
                                  />
                                  <button
                                    onClick={this.handleSendCalSyncEmail}
                                    className="input-with-button"
                                    disabled={emailHasError || !inputEmailValue}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                  {/* {transferPolicy && !servesAsWaitlist && ( */}
                  <div data-bem="AddStylist__stripe-container" className={CN.stripeContainer}>
                    <div className={CN.stripeTitle}>
                      <img src={STRIPE_LOGO} alt="stripe logo" className={CN.stripeLogo} />
                      {t('stylistScreen.paymentsAccount')}
                    </div>
                    <div className={CN.stripeLabel}>
                      <div>{t('stylistScreen.addStylist.pinCodeAcces.accountType')}</div>
                      <div className={CN.stripeBadge}>
                        <Badge variant={transferPolicy === TRANSFER_POLICY_TEXT_LOWER.noTransfer ? 'warning' : 'fb'}>
                          <Icon variant="stripeWhite" width="xs" />
                          <div className={CN.badgeFont}>{t(getTransferPolicyText(transferPolicy))}</div>
                        </Badge>
                      </div>
                    </div>
                    <div className={CN.stripeButtonContainer}>
                      <ButtonModal variant="outline" onClick={this.handleClickStripe} xSpace>
                        {transferPolicy === TRANSFER_POLICY_TEXT_LOWER.noTransfer
                          ? t('stylistScreen.addStylist.pinCodeAcces.setupStripeButton')
                          : t('stylistScreen.addStylist.pinCodeAcces.manageAccountButton')}
                      </ButtonModal>
                    </div>
                  </div>
                  {/* )} */}
                </div>
              )}

              {/* WAITLIST CHECKBOX*/}
              <ResponsiveBox.Mobile>
                {(isInternal || stylistId) && servesAsWaitlist && hasColumn && (
                  <div data-bem="AddStylist__waitlist-toggler">
                    <div className={CN.waitlistTogglerContainer}>
                      <span className={CN.waitlistTogglerLabel}>
                        {t('stylistScreen.addStylist.waitlistTogglerLabel')}
                      </span>
                      <Toggler onClick={this.setWaitList} selected={servesAsWaitlist} hideLabel />
                      <TooltipForReceptionist where={IN.inWaitlist} />
                    </div>
                    <div className={CN.waitlistDescription}>{t('stylistScreen.addStylist.waitlistDescription')}</div>
                  </div>
                )}
              </ResponsiveBox.Mobile>
              {/*  WAITLIST RULES CARD */}
              {servesAsWaitlist && (
                <div
                  className={cn({
                    [CN.cardWaitlistContainer]: true,
                    [CN.existingWaitlistSpace]: servesAsWaitlist,
                  })}
                >
                  <CardWaitlist tag="AddStylist__edit" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  stylists: state.onboarding.stylists.stylists,
  editedStylist: ownProps.stylist,
  // NOTE: the one from the state, if present, should be the latest from BE after a save
  stylist: selectOnboardingStylistsMap(state)[determineStylistId(ownProps)] || ownProps.stylist,
  emailsWithLogin: state.salon.stylistList.emailsWithLogin,
  salonId: state.currentSalon.id,
  pinCodes: state.salon.userPermission.pinCodes,
  stylistHasReceivedEmail: state.salon.stylistList.stylistHasReceivedEmail,
});

export const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setPin,
      togglePin,
      emailPin,
      getPin,
      resetPin,
      enableRemoteLogin,
      disableRemoteLogin,
      fetchStylist,
      fetchStylistsBySalon,
      toggleCalendarSync,
      feStripeFoldToggle, // Auto open fold Staff and stripe accounts
    },
    dispatch,
  );

const AddStylistHoc = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withTheme()(AddStylist))),
);

export default AddStylistHoc;
